import { h } from "preact";
import { useContext, useEffect, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { PersistContext } from "@/data/PersistProvider";
import { LanguageContext } from "@/data/LanguageProvider";
import { RechargeReturnContext } from "@/data/RechargeReturnContext";
import { isBankRecharge, resolveRechargeBusiness, bankRechargeReturnPath } from "@/utils/Bank";
import { ChannelRemind } from "@/utils/UrlHelper";

// Confirmed bank orders use their own receipt page. Ownership lookup must not
// block the existing checkout/status flow for old ordinary orders.
const BankRechargeReturn = ({ children }) => {
  const location = useLocation();
  const { getPersist, updatePersist } = useContext(PersistContext);
  const { langText } = useContext(LanguageContext);
  const user = getPersist("user_info");
  const loaded = getPersist("dataLoaded");
  const token = user?.token;
  const rid = user?.rid ?? user?.money?.rid;
  const orderId = new URLSearchParams(location.search).get("wait_order_id");
  const active = !!(user?.token && orderId && location.pathname.toLowerCase() !== "/bank");
  const key = active ? `${user.token}:${orderId}` : "";
  const knownBank = active && isBankRecharge(user, orderId);
  const snapshot = active && getPersist("recharge_attribution_order_snapshots")?.[orderId];
  const knownNormal = !!(!knownBank && snapshot && String(snapshot.owner_rid ?? snapshot.rid) === String(rid));
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!active || knownBank || knownNormal) return undefined;
    let current = true;
    setResult(null);
    resolveRechargeBusiness({ rid, token }, orderId).then((type) => {
      if (current) setResult({ key, type });
    });
    return () => { current = false; };
  }, [active, key, knownBank, knownNormal, orderId, rid, token]);

  const confirmedBank = knownBank || (result?.key === key && result.type === "BANK");
  useEffect(() => {
    if (!confirmedBank || !updatePersist) return;
    // An unidentified checkout may have restored an unverified legacy task.
    // Once ownership is known, remove only this bank order from that queue.
    updatePersist("bg_waitting_recharge_orders", (orders) => Array.isArray(orders)
      ? orders.filter((order) => String(order.order_id) !== String(orderId)) : orders);
    updatePersist("recharge_attribution_order_snapshots", (snapshots) => {
      if (!snapshots || !Object.prototype.hasOwnProperty.call(snapshots, orderId)) return snapshots;
      const next = { ...snapshots };
      delete next[orderId];
      return next;
    });
  }, [confirmedBank, orderId, updatePersist]);

  const paymentReturn = !!(orderId && location.pathname.toLowerCase() !== "/bank");
  if (paymentReturn && !loaded) return <div role="status">{langText("bank_checking_payment")}</div>;
  if (paymentReturn && !token) {
    return <Redirect to={ChannelRemind(`/login?back_to=${encodeURIComponent(location.pathname + location.search)}`)} />;
  }
  if (confirmedBank) {
    return <Redirect to={ChannelRemind(bankRechargeReturnPath(orderId))} />;
  }
  return <RechargeReturnContext.Provider value={{
    orderId: active ? orderId : null,
    businessType: !active || knownNormal ? "NORMAL" : result?.key === key ? result.type : null,
  }}>{children}</RechargeReturnContext.Provider>;
};

export default BankRechargeReturn;
